import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Typography,
  Image,
  Tag,
  Button,
  List,
  Rate,
  Statistic,
  Space,
  Divider,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  StarOutlined,
  UserOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import api from '../services/api';

const BACKEND_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(
  /\/api$/,
  '',
);

const getFullUrl = (url) => {
  if (!url) return '';
  // Only return directly if it starts with http:// or https:// (full URL with protocol)
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND_URL}${url}`;
};

const { Title, Text, Paragraph } = Typography;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: productData, isLoading: productLoading } = useQuery(
    ['product-detail', id],
    async () => {
      const response = await api.get(`/provider/products/${id}`);
      return response.data;
    },
  );

  const { isLoading: reviewsLoading } = useQuery(['product-reviews', id], async () => {
    const response = await api.get(`/provider/products/${id}`);
    return response.data;
  });

  const product = productData?.data || productData;
  const reviews = product?.reviews_only || product?.reviews || [];

  const allImages = useMemo(
    () => (product?.images?.length > 0 ? product.images : product?.media || []),
    [product],
  );
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (allImages.length > 0) {
      setSelectedImage(allImages[0]);
    }
  }, [productData, allImages]);

  const avgRating =
    reviews.length > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : 0;

  const getImageUrl = (image) => {
    if (!image) return '/placeholder.png';

    let url = '';

    // Handle object with url property
    if (image?.url) {
      url = image.url;

      // Check for malformed URLs like "http://localhost/storage/http://..."
      const match = url.match(/^https?:\/\/localhost\/storage\/(https?:\/\/.+)$/);
      if (match) {
        url = match[1]; // Extract the actual external URL
      }

      if (url.startsWith('http://') || url.startsWith('https://')) return url;
      return getFullUrl(url);
    }

    // Handle string URLs directly
    if (typeof image === 'string') {
      if (image.startsWith('http://') || image.startsWith('https://')) return image;
      return getFullUrl(image);
    }

    // Handle object with image_url property
    if (image?.image_url) {
      url = image.image_url;

      // Check for malformed URLs
      const match = url.match(/^https?:\/\/localhost\/storage\/(https?:\/\/.+)$/);
      if (match) {
        url = match[1];
      }

      if (url.startsWith('http://') || url.startsWith('https://')) return url;
      return getFullUrl('/storage/' + url);
    }

    // Handle media object from Laravel (has file_name)
    if (image?.file_name) {
      // Media object has id, file_name, model_id
      const fileId = image.id || image.model_id || '';
      url = '/storage/' + fileId + '/' + image.file_name;
      return getFullUrl(url);
    }

    return '/placeholder.png';
  };

  if (productLoading) {
    return <div style={{ padding: 24 }}>Loading...</div>;
  }

  if (!product) {
    return (
      <div style={{ padding: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/products')}>
          Back to Products
        </Button>
        <div style={{ marginTop: 24 }}>Product not found</div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/products')}
        style={{ marginBottom: 16 }}
      >
        Back to Products / Services
      </Button>

      <Row gutter={[24, 24]}>
        {/* Product Info */}
        <Col xs={24} lg={12}>
          <Card>
            {product?.images?.length > 0 || product?.media?.length > 0 ? (
              <div>
                <Image
                  src={getImageUrl(selectedImage)}
                  alt={product.name}
                  style={{ width: '100%', maxHeight: 400, objectFit: 'cover' }}
                  preview
                />
                {allImages.length > 1 && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                    {allImages.map((img, index) => (
                      <img
                        key={index}
                        src={getImageUrl(img)}
                        alt={`${product.name} ${index + 1}`}
                        onClick={() => setSelectedImage(img)}
                        style={{
                          width: 60,
                          height: 60,
                          objectFit: 'cover',
                          cursor: 'pointer',
                          border:
                            selectedImage === img ? '2px solid #1890ff' : '2px solid transparent',
                          borderRadius: 4,
                          opacity: selectedImage === img ? 1 : 0.6,
                        }}
                        onMouseEnter={(e) => {
                          if (selectedImage !== img) e.target.style.opacity = 0.8;
                        }}
                        onMouseLeave={(e) => {
                          if (selectedImage !== img) e.target.style.opacity = 0.6;
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div
                style={{
                  height: 300,
                  background: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Text type="secondary">No Image</Text>
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card>
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Title level={3} style={{ margin: 0 }}>
                {product.name}
              </Title>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => navigate(`/products?edit=${product.id}`)}
              >
                Edit
              </Button>
            </Space>

            <Divider />

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Price"
                  value={product.price}
                  prefix="Rs. "
                  valueStyle={{ color: '#1890ff', fontSize: 24 }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Type"
                  value={product.type}
                  valueStyle={{ textTransform: 'capitalize' }}
                />
              </Col>
            </Row>

            <Divider />

            <Row gutter={[16, 16]}>
              <Col span={12}>
                {product.type === 'product' ? (
                  <>
                    <Text strong>Stock: </Text>
                    <Tag
                      color={
                        product.stock_quantity > 10
                          ? 'green'
                          : product.stock_quantity > 0
                            ? 'orange'
                            : 'red'
                      }
                    >
                      {product.stock_quantity || 0}
                    </Tag>
                  </>
                ) : (
                  <>
                    <Text strong>Duration: </Text>
                    <Text>{product.booking_duration || 60} minutes</Text>
                  </>
                )}
              </Col>
              <Col span={12}>
                <Text strong>Status: </Text>
                {product.is_available ? (
                  <Tag color="green">
                    {product.type === 'service' ? 'Available for Booking' : 'Available'}
                  </Tag>
                ) : (
                  <Tag color="red">
                    {product.type === 'service' ? 'Not Available' : 'Out of Stock'}
                  </Tag>
                )}
              </Col>
            </Row>

            {product.category && (
              <div style={{ marginTop: 16 }}>
                <Text strong>Category: </Text>
                <Tag color="blue">{product.category.name}</Tag>
              </div>
            )}

            {product.description && (
              <div style={{ marginTop: 16 }}>
                <Text strong>Description:</Text>
                <Paragraph>{product.description}</Paragraph>
              </div>
            )}

            {product.type === 'service' && (
              <>
                <Divider>Booking Settings</Divider>
                <Row gutter={[16, 8]}>
                  <Col span={12}>
                    <Text>Booking Interval: {product.booking_interval || 30} min</Text>
                  </Col>
                  <Col span={12}>
                    <Text>Max Advance Days: {product.max_advance_days || 30}</Text>
                  </Col>
                  <Col span={12}>
                    <Text>Cancellation: {product.cancellation_hours || 24} hours</Text>
                  </Col>
                  <Col span={12}>
                    <Text>Reminder: {product.reminder_minutes || 60} min before</Text>
                  </Col>
                </Row>
              </>
            )}
          </Card>
        </Col>
      </Row>

      {/* Reviews Section */}
      <Card style={{ marginTop: 24 }}>
        <Title level={4}>
          <StarOutlined style={{ marginRight: 8 }} />
          Reviews ({reviews.length})
        </Title>

        {reviews.length > 0 ? (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={8}>
                <Card size="small">
                  <Statistic
                    title="Average Rating"
                    value={avgRating}
                    precision={1}
                    suffix="/ 5"
                    prefix={<Rate disabled value={avgRating} style={{ fontSize: 16 }} />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small">
                  <Statistic
                    title="Total Reviews"
                    value={reviews.length}
                    prefix={<StarOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small">
                  <Statistic
                    title="5-Star Reviews"
                    value={reviews.filter((r) => r.rating === 5).length}
                    valueStyle={{ color: '#10b981' }}
                  />
                </Card>
              </Col>
            </Row>

            <List
              loading={reviewsLoading}
              dataSource={reviews}
              renderItem={(review) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          background: '#f0f0f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <UserOutlined />
                      </div>
                    }
                    title={
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Text strong>{review.customer_name || 'Anonymous'}</Text>
                        <Rate disabled value={review.rating} style={{ fontSize: 14 }} />
                      </div>
                    }
                    description={
                      <div>
                        {review.comment && <div style={{ marginBottom: 8 }}>{review.comment}</div>}
                        <div style={{ color: '#888', fontSize: 12 }}>
                          <ClockCircleOutlined style={{ marginRight: 4 }} />
                          {dayjs(review.created_at).format('MMM D, YYYY HH:mm')}
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Text type="secondary">No reviews yet for this product</Text>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ProductDetail;
